import { useEffect, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Check, X, Sparkles, ArrowRight, ArrowLeft } from 'lucide-react';
import { useWalkthrough } from '@/contexts/WalkthroughContext';
import SpotlightTour from '@/components/setup/SpotlightTour';
import { scrollAppToTop } from '@/lib/scrollAppToTop';

export default function WalkthroughBar() {
  const { run, stops, currentStop, nextStop, prevStop, canGoBack, finishRun, exitRun } = useWalkthrough();
  const [tourOpen, setTourOpen] = useState(false);

  // Auto-play the page tour the first time each stop opens — one-shot per stop,
  // tracked in sessionStorage so a refresh doesn't replay it.
  useEffect(() => {
    if (!run || !currentStop) return;
    const seenKey = `ldg-wt-tour-${run.index}`;
    if (currentStop.tourSteps.length && !sessionStorage.getItem(seenKey)) {
      sessionStorage.setItem(seenKey, '1');
      setTourOpen(true);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [run?.index, currentStop]);

  if (!run || !currentStop) return null;

  // Clamp: a restored run index can exceed the stop list if feature gates
  // changed since the run was stored.
  const index = Math.min(run.index, stops.length - 1);
  const isLast = index >= stops.length - 1;
  const nextLabel = !isLast ? stops[index + 1]?.label : null;

  const nextButton = (extraClass: string) => (
    <Button
      className={`font-semibold rounded-xl shadow-sm bg-[#E51636] text-white hover:bg-[#c91230] ${extraClass}`}
      style={{ animation: 'ldg-next-pulse 1.8s ease-in-out infinite' }}
      onClick={isLast ? finishRun : nextStop}
    >
      {isLast
        ? (<><Check className="w-4 h-4 mr-1.5" /> All done!</>)
        : (<span className="truncate">Next: {nextLabel}</span>)
      }
      {!isLast && <ArrowRight className="w-4 h-4 ml-1.5 shrink-0" />}
    </Button>
  );

  return (
    <>
      {/* The bar hides while a tooltip tour is showing — the tour has its own
          Next/Skip, and hiding frees the whole screen (on mobile the bar
          overlays the app header, which some tour steps need to spotlight). */}
      {!tourOpen && (
        <div
          data-walkthrough-bar
          className="fixed left-0 right-0 top-0 z-[1080] min-[938px]:sticky min-[938px]:top-[calc(4rem+env(safe-area-inset-top))] min-[938px]:z-40 bg-white border-b-2 border-[#E51636] shadow-sm px-3 py-2 min-[938px]:px-4 min-[938px]:py-3"
        >
          <style>{'@keyframes ldg-next-pulse{0%,100%{box-shadow:0 0 0 0 rgba(229,22,54,0.45)}50%{box-shadow:0 0 0 7px rgba(229,22,54,0)}}'}</style>

          {/* Mobile: compact two rows, overlaying the app header */}
          <div className="min-[938px]:hidden max-w-6xl mx-auto">
            <div className="flex items-center gap-2">
              <p className="flex-1 min-w-0 truncate text-xs font-semibold text-[#E51636] uppercase tracking-wide">
                Tour {index + 1}/{stops.length} · {currentStop.label}
              </p>
              <button onClick={exitRun} aria-label="Finish later" className="p-1 -mr-1 text-gray-400 hover:text-gray-600">
                <X className="w-4 h-4" />
              </button>
            </div>
            <div className="mt-1.5 flex items-center gap-1.5">
              {canGoBack && (
                <Button size="sm" variant="ghost" className="px-2 text-gray-500" onClick={prevStop} aria-label="Back">
                  <ArrowLeft className="w-4 h-4" />
                </Button>
              )}
              {currentStop.tourSteps.length > 0 && (
                <Button size="sm" variant="ghost" className="px-2 text-[#E51636]" onClick={() => setTourOpen(true)} aria-label="Show me around">
                  <Sparkles className="w-4 h-4" />
                </Button>
              )}
              {nextButton('flex-1 min-w-0 h-9 px-3 text-sm')}
            </div>
          </div>

          {/* Desktop: the original single-row layout below the app header */}
          <div className="hidden min-[938px]:flex max-w-6xl mx-auto items-center gap-4">
            <div className="flex-1 min-w-0">
              <p className="text-xs font-semibold text-[#E51636] uppercase tracking-wide">
                Tour — {index + 1} of {stops.length}: {currentStop.label}
              </p>
              <p className="text-sm text-gray-600 truncate">{currentStop.nudge}</p>
            </div>
            <div className="flex items-center gap-2">
              {canGoBack && (
                <Button size="sm" variant="ghost" className="text-gray-500" onClick={prevStop}>
                  <ArrowLeft className="w-4 h-4 mr-1" /> Back
                </Button>
              )}
              {currentStop.tourSteps.length > 0 && (
                <Button size="sm" variant="ghost" className="text-[#E51636]" onClick={() => setTourOpen(true)}>
                  <Sparkles className="w-4 h-4 mr-1" /> Show me around
                </Button>
              )}
              {nextButton('px-5 h-10')}
              <button
                className="text-xs text-gray-400 hover:text-gray-600 flex items-center gap-1 ml-1"
                onClick={exitRun}
              >
                <X className="w-3 h-3" /> Finish later
              </button>
            </div>
          </div>
        </div>
      )}

      {tourOpen && currentStop.tourSteps.length > 0 && (
        <SpotlightTour
          steps={currentStop.tourSteps}
          onClose={() => {
            setTourOpen(false);
            // Tours often end deep in the page (the checklist celebration sits
            // on the bottom progress bar) — bring the user back to the top so
            // the page and the Next button read as a fresh start.
            scrollAppToTop(true);
          }}
        />
      )}
    </>
  );
}
